// ============================================================
// main.js — Entry point for The Amazing Digital Clicker
// ============================================================

// --- Styles ---
import './styles/base.css';
import './styles/fire.css';
import './styles/frozen.css';
import './styles/phase3.css';
import './styles/phase4.css';
import './styles/phase5.css';
import './styles/shop.css';
import './styles/ui.css';
import './styles/overlays.css';
import './styles/eyeBoss.css';
import './styles/mobile.css';

// --- Foundation ---
import { state } from './state.js';
import { dom } from './dom.js';

// --- Audio ---
import { initAudio } from './audio/context.js';
import { playClickSound } from './audio/sfx.js';
import { initAudioSettings, fadeOutAllMusic, pauseMusic, resumeMusic, stopAllMusic, startCurrentMusic } from './audio/manager.js';

// --- Phase orchestrator ---
import { providePhasesDeps, checkPhaseTransitions } from './phases.js';

// --- Systems ---
import { initCursor } from './systems/cursor.js';
import { spawnParticle, spawnRingBurst } from './systems/effects.js';
import { stopFireEffect } from './systems/fire.js';
import { activateBombMode } from './systems/bombs.js';
import { activateFrozenMode, deactivateFrozenMode } from './systems/frozen.js';
import { activatePhase3, deactivatePhase3 } from './systems/phase3.js';
import { activatePhase4, deactivatePhase4 } from './systems/phase4.js';
import { activatePhase5, deactivatePhase5, updateEscapeProgress } from './systems/phase5.js';
import { activateEyeBoss, deactivateEyeBoss, resetEyeBossTrigger } from './systems/eyeBoss.js';
import { initShop, updateShopUI, restartAutoIntervals, spawnAutoMouse } from './systems/shop.js';
import { pauseGame, startResumeCountdown } from './systems/pause.js';
import { applyHazardDamage, loseLife, resetLives, showLivesUI, startShieldSpawning, stopShieldSpawning } from './systems/competitive.js';
import { triggerWin, resetGame, updateBadgesUI, initWinHandlers, provideWinDeps, clearHazards } from './systems/win.js';
import { saveGame, loadGame, initAutoSave, provideSaveDeps } from './systems/save.js';
import { initMenu, goToMenu, setGameUIVisible, provideMenuDeps } from './systems/menu.js';
import { initAuth } from './systems/auth.js';
import { initLeaderboard } from './systems/leaderboard.js';

// ============================================================
// Wire up circular dependencies via dependency injection
// ============================================================

providePhasesDeps({
  activateBombMode,
  activateFrozenMode,
  activatePhase3,
  activatePhase4,
  activatePhase5,
  activateEyeBoss,
  updateEscapeProgress,
  triggerWin,
});

provideSaveDeps({
  activateBombMode,
  activateFrozenMode,
  activatePhase3,
  activatePhase4,
  activatePhase5,
});

provideWinDeps({
  deactivatePhase5,
  deactivatePhase4,
  deactivatePhase3,
  deactivateEyeBoss,
  resetEyeBossTrigger,
  stopFireEffect,
  resetLives,
  showLivesUI,
  stopShieldSpawning,
  startShieldSpawning,
  goToMenu,
});

provideMenuDeps({
  resetGame,
  deactivatePhase5,
  resetLives,
  showLivesUI,
  stopShieldSpawning,
  startShieldSpawning,
});

// ============================================================
// Initialize systems
// ============================================================

initCursor();
initAudioSettings();
initShop();
initWinHandlers();
initAutoSave();
initMenu();
initLeaderboard();

// Async init runs in the background so a slow/unreachable Supabase never
// blocks the module from finishing. Each step is bounded by a timeout and
// its own catch so a single failure can't stop the chain — the user still
// gets a playable game (guest mode, no cloud save) if Supabase is down.
const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);

withTimeout(initAuth(), 8000, 'initAuth')
  .catch((e) => console.warn('[init] auth failed, continuing as guest:', e?.message || e))
  .then(() => withTimeout(loadGame(), 5000, 'loadGame'))
  .catch((e) => console.warn('[init] load failed, starting fresh:', e?.message || e));

// Dev-only console hook
if (import.meta.env.DEV) {
  window.spawnEyeBoss = () => {
    state.eyeBossTriggered = false;
    state.eyeBossActive = false;
    state.eyeBossHits = 0;
    activateEyeBoss();
  };
}

// ============================================================
// Main click handler (on plushie)
// ============================================================

dom.smiley.addEventListener('click', (e) => {
  if (state.dead || state.escaped) return;

  // Unfreeze on first click after continuing from menu
  if (state.awaitingFirstClick) {
    state.awaitingFirstClick = false;
    state.paused = false;
    document.body.classList.remove('awaiting-first-click');
    initAudio();
    // Start music fresh (it was fully stopped on Save & Menu) if user has it on
    if (dom.toggleMusic.classList.contains('on')) {
      startCurrentMusic();
    }
    // Re-arm auto-clickers (they were stopped when returning to menu)
    if (state.autoClickerCount > 0) {
      // Rebuild the orbiting mouse sprites if they were cleared
      if (state.autoMouseElements.length < state.autoClickerCount) {
        const missing = state.autoClickerCount - state.autoMouseElements.length;
        for (let i = 0; i < missing; i++) spawnAutoMouse();
      }
      restartAutoIntervals();
    }
    // Re-trigger phase activations for the current score so hazards resume
    checkPhaseTransitions();
    // Fall through to process this click normally
  }

  if (state.paused) return;

  // Ensure audio is ready on first interaction
  initAudio();

  state.score += state.clickPower;
  dom.scoreEl.textContent = state.score;
  playClickSound();
  updateShopUI();

  // Check all phase transitions
  checkPhaseTransitions();
  if (state.escaped) return; // triggerWin was called

  // Combo system
  state.combo++;
  clearTimeout(state.comboTimer);
  state.comboTimer = setTimeout(() => {
    state.combo = 0;
    dom.comboEl.classList.remove('show');
  }, 1200);

  if (state.combo >= 3) {
    dom.comboEl.textContent = `${state.combo}x Combo!`;
    dom.comboEl.classList.add('show');
    dom.comboEl.style.fontSize = Math.min(20 + state.combo * 2, 40) + 'px';
  }

  // Spawn effects
  const rect = dom.smiley.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  spawnParticle(cx, cy);

  // Quick bounce on score
  dom.scoreEl.style.transform = 'scale(1.3)';
  setTimeout(() => dom.scoreEl.style.transition = 'transform 0.2s ease', 10);
  setTimeout(() => {
    dom.scoreEl.style.transform = 'scale(1)';
    setTimeout(() => dom.scoreEl.style.transition = '', 200);
  }, 100);
});

// ============================================================
// Pause button
// ============================================================

let countdownActive = false;

dom.pauseBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  if (state.dead || state.escaped || countdownActive) return;
  if (state.paused) {
    startResumeCountdown();
  } else {
    pauseMusic(600);
    pauseGame();
  }
});

dom.resumeBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  if (!state.paused || countdownActive) return;
  startResumeCountdown();
});

// Save & go to menu from pause — uses the same smooth pause mechanic
function performExitToMenu() {
  // Pause the whole game smoothly, just like clicking the pause button
  state.paused = true;
  dom.smiley.style.pointerEvents = 'none';
  pauseMusic(600);

  // Only save in normal mode — competitive always starts fresh
  if (state.gameMode === 'normal') saveGame();

  // After the smooth pause completes, transition to menu
  setTimeout(() => {
    dom.pauseOverlay.classList.remove('show');
    dom.pauseBtn.innerHTML = '&#x23F8;';
    // Now fully stop music (we're leaving the game for good)
    stopAllMusic();
    // Eliminate every active attack / hazard and stop auto-click grinding
    clearHazards();
    if (state.gameMode === 'competitive') {
      resetGame();
    }
    state.paused = false;
    goToMenu();
  }, 650);
}

const saveMenuBtn = document.getElementById('save-menu-btn');
if (saveMenuBtn) {
  saveMenuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.gameMode === 'competitive') {
      // Show warning first
      dom.exitWarningOverlay.classList.add('show');
    } else {
      performExitToMenu();
    }
  });
}

// Exit warning: cancel
dom.exitWarningCancel.addEventListener('click', (e) => {
  e.stopPropagation();
  dom.exitWarningOverlay.classList.remove('show');
});

// Exit warning: confirm — leave anyway
dom.exitWarningConfirm.addEventListener('click', (e) => {
  e.stopPropagation();
  dom.exitWarningOverlay.classList.remove('show');
  performExitToMenu();
});
